/**
 * AppRouter: the application route table.
 *
 * Extracted from the former monolithic `App.tsx` (audit A1 / item 28). Holds
 * the public, interview, dev, and protected-admin routes. The respondent flow
 * lives in `InterviewFlow`, the admin DB-write surface in `AdminSetupPage`,
 * and the provider tree + boot wiring stays in `App.tsx`.
 *
 * @module components/AppRouter
 */

import React from 'react';
import { Routes, Route } from 'react-router-dom';

import { LoginPage, ProtectedRoute, OAuthConsentPage } from './auth';
import { AdminDashboard, InterviewDetailView, AdminLayout } from './admin';
import { AcceptInvitePage } from './invite/AcceptInvitePage';
import { SettingsPage } from './admin/settings/SettingsPage';
import { BillingPage } from './admin/BillingPage';
import { WorkspaceSettings } from './workspace/WorkspaceSettings';
import RoomJoinPage from './RoomJoinPage';
import EmbedLauncherPage from './embed/EmbedLauncherPage';
import { InterviewFlow } from './InterviewFlow';
import { AdminSetupPage } from './AdminSetupPage';

/**
 * Dev-only route group (`/dev/*`). Lazy-loaded and only mounted outside
 * production so no `/dev/*` code ships in the prod bundle. Add new
 * playgrounds inside `DevRoutes`, the gate here covers all of them.
 *
 * This comparison is kept as a literal `import.meta.env.VITE_ENVIRONMENT ===
 * 'production'` on purpose: Vite statically inlines it at build time, so the
 * `false` branch (and the lazy `import('./dev/DevRoutes')`) is tree-shaken out
 * of the prod bundle. The runtime equivalent is `IS_PROD` from
 * `lib/utils/env/is-prod.ts`, but using it here would defeat tree-shaking, so
 * the literal must stay.
 */
const IS_PROD = import.meta.env.VITE_ENVIRONMENT === 'production';
const AdminRecipePage = React.lazy(() => import('./admin/AdminRecipePage'));
const DevRoutes = IS_PROD ? null : React.lazy(() => import('./dev/DevRoutes'));

/** The full application route table. */
export const AppRouter: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<RoomJoinPage />} />
      <Route path="/join/:hash" element={<RoomJoinPage />} />
      <Route path="/embed/:hash" element={<RoomJoinPage embedMode />} />
      {/* Tiny launcher page loaded inside the snippet's small "orb-mode" iframe.
          Renders the orb + a Start button; click posts `hollie:launcher_open`
          to the parent so the snippet swaps to the full modal. No SPA chrome. */}
      <Route path="/embed/launcher/:hash" element={<EmbedLauncherPage />} />
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      <Route path="/login" element={<LoginPage />} />

      {/* OAuth 2.1 consent screen: the authorization path Supabase's OAuth
          server redirects to. Protected, so an unauthenticated caller is sent
          to login and returned here with the same authorization_id. */}
      <Route
        path="/oauth/consent"
        element={
          <ProtectedRoute>
            <OAuthConsentPage />
          </ProtectedRoute>
        }
      />

      {/* Interview Route (public but validates access) */}
      <Route path="/interview/:roomId" element={<InterviewFlow />} />

      {/* Dev playgrounds - local + staging only, stripped from prod bundle. */}
      {DevRoutes && (
        <Route
          path="/dev/*"
          element={
            <React.Suspense fallback={null}>
              <DevRoutes />
            </React.Suspense>
          }
        />
      )}

      {/* Protected Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <SettingsPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/billing"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <BillingPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/workspace/settings"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <WorkspaceSettings />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/setup"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminSetupPage />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/admin/recipes/:recipeId" element={<ProtectedRoute><AdminLayout>
        <React.Suspense fallback={null}><AdminRecipePage /></React.Suspense>
      </AdminLayout></ProtectedRoute>} />
      <Route
        path="/admin/interviews/:id"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <InterviewDetailView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/interviews/:id/edit"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <InterviewDetailView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/interviews/:id/responses/:responseId"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <InterviewDetailView />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default AppRouter;
